import { useState } from "react";
import { X, Send, CheckCircle2, Loader2, Phone } from "lucide-react";
import api from "../lib/api";
import { CONTACT } from "./marketingData";

const EMPTY = { name: "", phone: "", email: "", company: "", cargo: "", message: "" };

export function QuoteForm({ open, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim()) {
      setError("Please enter your name and phone number.");
      return;
    }
    setSending(true);
    try {
      await api.post("/leads", { ...form, source: "website" });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not send. Please call us instead.");
    } finally {
      setSending(false);
    }
  }

  function close() {
    onClose();
    setTimeout(() => { setForm(EMPTY); setDone(false); setError(""); }, 250);
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="quote-form-modal">
      <div className="absolute inset-0 bg-[#1A1A18]/70 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-lg bg-[#F9F8F6] rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto">
        <button onClick={close} data-testid="quote-form-close"
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-[#1A1A18]/5 hover:bg-[#1A1A18]/10 flex items-center justify-center text-[#1A1A18]">
          <X className="w-5 h-5" />
        </button>

        {done ? (
          <div className="p-10 text-center" data-testid="quote-form-success">
            <div className="w-16 h-16 rounded-full bg-[#EAF2ED] flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-8 h-8 text-[#2D523B]" />
            </div>
            <h3 className="font-heading font-bold text-2xl text-[#1A1A18]">Thank you — we'll call you back.</h3>
            <p className="mt-3 text-[#4F4C44]">Our team has received your enquiry and will reach out shortly. For anything urgent, call us directly.</p>
            <a href={CONTACT.phoneHref} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#C84B31] px-6 py-3 text-sm font-semibold text-white hover:bg-[#a83c26] transition-colors">
              <Phone className="w-4 h-4" /> {CONTACT.phone}
            </a>
          </div>
        ) : (
          <form onSubmit={submit} className="p-7 sm:p-9">
            <p className="text-xs font-semibold tracking-widest uppercase text-[#C84B31] mb-2">Request a callback</p>
            <h3 className="font-heading font-bold text-2xl sm:text-3xl text-[#1A1A18] leading-tight">Need a quote? Tell us what needs moving.</h3>
            <p className="mt-2 text-sm text-[#4F4C44]">Share a few details and our team will call you back with a quote — by road or air, to any pincode in India.</p>

            <div className="mt-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Your name *" testid="quote-name" value={form.name} onChange={set("name")} placeholder="Full name" />
                <Field label="Phone *" testid="quote-phone" value={form.phone} onChange={set("phone")} placeholder="Mobile number" type="tel" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Email" testid="quote-email" value={form.email} onChange={set("email")} placeholder="you@company.com" type="email" />
                <Field label="Company" testid="quote-company" value={form.company} onChange={set("company")} placeholder="Company name" />
              </div>
              <Field label="What are you moving?" testid="quote-cargo" value={form.cargo} onChange={set("cargo")} placeholder="e.g. 2 transformers, Mumbai → Leh" />
              <div>
                <label className="block text-xs font-semibold text-[#68665E] mb-1.5">Message</label>
                <textarea data-testid="quote-message" value={form.message} onChange={set("message")} rows={3}
                  placeholder="Weight, dimensions, timelines, anything else…"
                  className="w-full rounded-xl border border-[#1A1A18]/15 bg-white px-4 py-2.5 text-sm text-[#1A1A18] focus:border-[#C84B31] focus:ring-1 focus:ring-[#C84B31] outline-none resize-none" />
              </div>
            </div>

            {error && <p className="mt-4 text-sm text-[#C84B31]" data-testid="quote-error">{error}</p>}

            <button type="submit" disabled={sending} data-testid="quote-submit"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C84B31] px-6 py-3.5 text-sm font-semibold text-white hover:bg-[#a83c26] transition-colors disabled:opacity-60">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "Sending…" : "Request callback"}
            </button>
            <p className="mt-3 text-center text-xs text-[#68665E]">Prefer to talk? Call <a href={CONTACT.phoneHref} className="text-[#C84B31] font-medium">{CONTACT.phone}</a></p>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, placeholder, type = "text" }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-[#68665E] mb-1.5">{label}</label>
      <input type={type} data-testid={testid} value={value} onChange={onChange} placeholder={placeholder}
        className="w-full rounded-xl border border-[#1A1A18]/15 bg-white px-4 py-2.5 text-sm text-[#1A1A18] focus:border-[#C84B31] focus:ring-1 focus:ring-[#C84B31] outline-none" />
    </div>
  );
}
